import React, { useEffect, useRef } from 'react';
import { BreadcrumbsManager } from '../../../context/BreadcrumbsManager';
import Contact from '../../../models/Contact';
import Investor from '../../../models/Investor';
import { fetchInvestorAndContact } from './adminEntityAssociationWizardData';
import { AdminEntityAssociationWizardState } from './adminEntityAssociationWizardTypes';

interface BootstrapParams {
  investorId?: string;
  selectedEntityId?: string;
  currentState?: string;
  contact: Contact | null;
  investor: Investor | null;
  currentStep: AdminEntityAssociationWizardState['currentStep'];
  loading: boolean;
  setState: React.Dispatch<React.SetStateAction<AdminEntityAssociationWizardState>>;
  loadEntities: (
    contact: Contact | null,
    investor: Investor | null,
    opts?: { navigateOnInit?: boolean; silent?: boolean },
  ) => Promise<void>;
  refreshInvestorData: () => Promise<unknown>;
}

export function useAdminEntityAssociationWizardEffects({
  investorId,
  selectedEntityId,
  currentState,
  contact,
  investor,
  currentStep,
  loading,
  setState,
  loadEntities,
  refreshInvestorData,
}: BootstrapParams) {
  const initialEntitiesLoadedRef = useRef(false);

  useEffect(() => {
    BreadcrumbsManager.clear();
    BreadcrumbsManager.push({ label: 'Admin Dashboard', href: '/' });
    BreadcrumbsManager.push({
      label: 'Associate Investment Entity',
      href: `/associate-entity/${investorId}${selectedEntityId ? `/${selectedEntityId}` : ''}${currentState ? `/${currentState}` : ''}`,
    });

    if (!investorId) {
      setState((prev) => ({
        ...prev,
        error: 'No investor ID provided',
        loading: false,
      }));
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const result = await fetchInvestorAndContact(investorId);
        if (cancelled) return;
        setState((prev) => ({
          ...prev,
          investor: result.investor,
          contact: result.contact,
          loading: false,
        }));
      } catch (e) {
        if (cancelled) return;
        console.error(e);
        setState((prev) => ({
          ...prev,
          error: 'Unable to load investor information',
          loading: false,
        }));
      }
    })();

    return () => {
      cancelled = true;
      setState((prev) => ({ ...prev, loading: false }));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investorId]);

  useEffect(() => {
    if (!contact || !investor) return;
    if (initialEntitiesLoadedRef.current) return;

    void loadEntities(contact, investor, { navigateOnInit: !currentState });
    initialEntitiesLoadedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact, investor]);

  useEffect(() => {
    if (currentStep === 'allocation' && investor && !loading) {
      void refreshInvestorData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, investor?.id]);
}
